import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from '../../lib/toast';
import Icon from '../../components/dashboard/DashboardIcons';
import { useParentData } from '../../context/ParentDataContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const RECEIPT_STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-700 border border-amber-200',
  confirmed: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border border-red-200',
};
const RECEIPT_STATUS_LABELS = { pending: 'قيد المراجعة', confirmed: 'مؤكّد', rejected: 'مرفوض' };
const PAYMENT_STATUS_LABELS = { paid: 'مدفوع', partial: 'مدفوع جزئياً', unpaid: 'غير مدفوع' };

const UploadReceiptModal = ({ playerId, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const submit = async () => {
    if (!file) { toast.error('اختر ملف الإيصال أولاً (PDF أو صورة)'); return; }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      if (amount) form.append('amount', amount);
      if (note) form.append('note', note);
      await axios.post(`${API}/parent/me/child/${playerId}/receipt`, form, {
        headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('تم رفع الإيصال، بانتظار مراجعة الأكاديمية');
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'فشل رفع الإيصال');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="parent-upload-receipt-modal">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-black text-slate-900">رفع إيصال دفع</h4>
          <button onClick={onClose} data-testid="parent-upload-receipt-close"><Icon name="x" className="h-5 w-5 text-slate-400" /></button>
        </div>
        <div className="mt-4 space-y-3">
          <button
            onClick={() => fileRef.current?.click()}
            data-testid="parent-upload-receipt-file-btn"
            className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 py-6 text-sm font-semibold text-slate-500 hover:border-teal-400 hover:text-teal-600"
          >
            <Icon name="upload" className="h-6 w-6" />
            {file ? file.name : 'اختر ملف PDF أو JPG أو PNG'}
          </button>
          <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="parent-upload-receipt-file-input" />
          <div>
            <label className="mb-1 block text-xs font-bold text-slate-600">المبلغ (اختياري)</label>
            <input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="parent-upload-receipt-amount" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-teal-500 focus:outline-none" placeholder="0" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold text-slate-600">ملاحظة (اختياري)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="parent-upload-receipt-note" rows={2} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-teal-500 focus:outline-none" />
          </div>
        </div>
        <div className="mt-5 flex gap-2">
          <button onClick={onClose} className="flex-1 rounded-xl border border-slate-200 py-2.5 text-sm font-semibold text-slate-600">إلغاء</button>
          <button onClick={submit} disabled={submitting} data-testid="parent-upload-receipt-submit" className="flex-1 rounded-xl bg-teal-600 py-2.5 text-sm font-bold text-white disabled:opacity-60">
            {submitting ? 'جاري الرفع...' : 'رفع الإيصال'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function ParentPaymentsPage() {
  const { childrenList } = useParentData() || {};
  const [selected, setSelected] = useState(null);
  const [finance, setFinance] = useState(null);
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => {
    if (childrenList && childrenList.length > 0 && !selected) setSelected(childrenList[0].player_id);
  }, [childrenList, selected]);

  const loadFinance = useCallback(async () => {
    if (!selected) return;
    setFinance(null);
    try {
      const res = await axios.get(`${API}/parent/me/child/${selected}/finance`, auth());
      setFinance(res.data);
    } catch { setFinance(false); }
  }, [selected]);

  useEffect(() => { loadFinance(); }, [loadFinance]);

  const copyIban = (iban) => {
    navigator.clipboard.writeText(iban.replace(/\s/g, ''));
    toast.success('تم نسخ رقم الآيبان');
  };

  const downloadReceipt = async (id, filename) => {
    try {
      const res = await axios.get(`${API}/parent/me/receipt/${id}/download`, { ...auth(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = filename || 'receipt'; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('تعذر تحميل الإيصال'); }
  };

  if (!childrenList) {
    return <div className="space-y-4" data-testid="parent-payments-page">{[1, 2].map((i) => <div key={i} className="h-24 animate-pulse rounded-2xl bg-slate-100" />)}</div>;
  }

  if (childrenList.length === 0) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="parent-payments-page">
        <Icon name="wallet" className="h-12 w-12 text-slate-300" />
        <p className="mt-4 text-sm font-bold text-slate-700">لا يوجد أبناء مرتبطين بحسابك لعرض بياناتهم المالية</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="parent-payments-page">
      <h1 className="text-xl font-black text-[#0A192F]">المالية والمدفوعات</h1>

      {childrenList.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="parent-payments-child-selector">
          {childrenList.map((c) => (
            <button
              key={c.player_id}
              onClick={() => setSelected(c.player_id)}
              data-testid={`parent-payments-child-tab-${c.player_id}`}
              className={`rounded-xl px-4 py-2 text-sm font-bold transition-all ${selected === c.player_id ? 'bg-teal-600 text-white shadow-sm' : 'bg-white text-slate-500 border border-slate-200'}`}
            >
              {c.player_name}
            </button>
          ))}
        </div>
      )}

      {finance === null ? (
        <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-100" />)}</div>
      ) : finance === false ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700">تعذر تحميل البيانات المالية</div>
      ) : (
        <>
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="parent-finance-summary">
            <div className="mb-1 text-sm font-bold text-slate-500">{finance.club_name}</div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="text-lg font-black text-[#0A192F]">{finance.subscription_amount} ر.س</div>
                <div className="text-[11px] text-slate-400">القيمة الكلية</div>
              </div>
              <div>
                <div className="text-lg font-black text-emerald-600">{finance.subscription_paid_amount} ر.س</div>
                <div className="text-[11px] text-slate-400">المدفوع</div>
              </div>
              <div>
                <div className="text-lg font-black text-red-500">{finance.subscription_remaining} ر.س</div>
                <div className="text-[11px] text-slate-400">المتبقي</div>
              </div>
            </div>
            {finance.subscription_payment_status && (
              <div className="mt-3 text-center">
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">{PAYMENT_STATUS_LABELS[finance.subscription_payment_status] || finance.subscription_payment_status}</span>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-700"><Icon name="banknotes" className="h-4 w-4 text-teal-500" /> طرق الدفع المتاحة</h3>
            {finance.bank_accounts.length === 0 ? (
              <p className="text-xs text-slate-400">لم تُضف الأكاديمية حسابات بنكية بعد. للدفع تواصل مباشرة: {finance.club_phone || finance.club_email || 'لا تتوفر بيانات تواصل'}</p>
            ) : (
              <div className="space-y-2" data-testid="parent-bank-accounts-list">
                {finance.bank_accounts.map((b, i) => (
                  <div key={i} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5">
                    <div>
                      <div className="text-sm font-bold text-slate-800">{b.bank_name} {b.is_default && <span className="mr-1 rounded-full bg-teal-50 px-1.5 py-0.5 text-[10px] text-teal-600">افتراضي</span>}</div>
                      <div className="text-xs text-slate-500">{b.beneficiary_name}</div>
                      <div dir="ltr" className="mt-0.5 text-xs font-mono text-slate-600">{b.iban}</div>
                    </div>
                    <button onClick={() => copyIban(b.iban)} data-testid={`parent-copy-iban-${i}`} className="rounded-lg border border-slate-200 p-2 text-slate-400 hover:text-teal-600"><Icon name="copy" className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-[11px] text-amber-700">الدفع الإلكتروني المباشر — قريباً</div>
            <button onClick={() => setShowUpload(true)} data-testid="parent-upload-receipt-btn" className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-teal-600 py-2.5 text-sm font-bold text-white hover:bg-teal-700">
              <Icon name="upload" className="h-4 w-4" /> رفع إيصال دفع
            </button>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-700"><Icon name="document" className="h-4 w-4 text-teal-500" /> الإيصالات المرفوعة</h3>
            {finance.receipts.length === 0 ? (
              <p className="text-xs text-slate-400">لم تُرفع أي إيصالات بعد</p>
            ) : (
              <div className="space-y-2" data-testid="parent-receipts-list">
                {finance.receipts.map((r) => (
                  <div key={r.id} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5">
                    <div>
                      <div className="text-sm font-bold text-slate-800">{r.filename}{r.amount ? ` · ${r.amount} ر.س` : ''}</div>
                      <div className="text-xs text-slate-500">{new Date(r.submitted_at).toLocaleDateString('ar-SA')}</div>
                      {r.status === 'rejected' && r.rejection_reason && <div className="mt-1 text-[11px] text-red-600">سبب الرفض: {r.rejection_reason}</div>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${RECEIPT_STATUS_STYLES[r.status]}`}>{RECEIPT_STATUS_LABELS[r.status]}</span>
                      <button onClick={() => downloadReceipt(r.id, r.filename)} data-testid={`parent-download-receipt-${r.id}`} className="rounded-lg border border-slate-200 p-2 text-slate-400 hover:text-teal-600"><Icon name="download" className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {showUpload && selected && (
        <UploadReceiptModal playerId={selected} onClose={() => setShowUpload(false)} onSuccess={() => { setShowUpload(false); loadFinance(); }} />
      )}
    </div>
  );
}
